import { Alert, Button, DatePicker, Form, Select, Table, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import {
  AccountBean,
  DspBean,
  KeyFilterClause,
  PublisherBean,
  RawReportKeyColumnDescription,
  RawReportRequestParameters,
} from "src/api/api";
import reportApi from "../reports/ReportApi";
import { calculateColumnsWidth } from "../rawReports/dynamicColumnsHelper";
import DownloadOutlined from "@ant-design/icons/lib/icons/DownloadOutlined";
import moment from "dayjs";
import "./style.css";
import { headerDescription } from "./ReportUtil";
import { isAdmin, isReporting } from "../../../util/Util";
import { useUser } from "../../../context/UserContext";
import { formDataLoader, FormDataType } from "../../../util/FormDataLoader";

const { RangePicker } = DatePicker;

const dateFormat = "YYYY-MM-DDTHH:00:00";

const formInfoItems: FormDataType[] = [
  FormDataType.Publishers,
  FormDataType.DspList,
];

const FormItem = Form.Item;
const Option = Select.Option;
const RawKeyColumns = RawReportKeyColumnDescription.ColumnEnum;

export const RequestResponseIds: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [disableRequestId, setDisableRequestId] = useState(false);
  const [form] = Form.useForm();

  const [blobCsv, setBlobCsv] = useState<any>();
  const [header, setHeader] = useState<string[]>();
  const [data, setData] = useState<string[][]>();
  const [dates, setDates] = useState<[moment.Dayjs, moment.Dayjs]>();
  const { user } = useUser();
  const admin = isAdmin(user);
  const reporting = isReporting(user);
  const [error, setError] = useState(undefined);
  const [info, setInfo] = useState({
    publishers: [],
    dspList: [],
  });

  const onRejected = (err) => {
    setError(err.error);
  };

  const loadData = async () => {
    try {
      if (admin || reporting) {
        let formInfo = await formDataLoader(...formInfoItems);
        setInfo(formInfo as any);
      }
    } catch (e) {
      onRejected(e);
    }
  };

  useEffect(() => {
    loadData();
  }, [admin, reporting]);

  const getGroupKeyColumns = (
    cols: string[],
    formValues: any,
    reportType: RawReportRequestParameters.ReportTypeEnum
  ) => {
    const res: RawReportKeyColumnDescription[] = [];

    let dateCol: RawReportKeyColumnDescription;
    dateCol = {
      column: RawKeyColumns.Timestamp,
      where: {
        type: KeyFilterClause.TypeEnum.Between,
        value: formValues.dates.map((d) => d.format(dateFormat)),
      },
    };
    dateCol.onlyFilter = true;
    res.push(dateCol);

    res.push({
      column: RawKeyColumns.ValidationPathGroup,
    });

    if (admin || reporting) {
      res.push({
        column: RawKeyColumns.PublisherIdGroup,
      });
    }

    if (user?.role === AccountBean.RoleEnum.Publisher) {
      res.push({
        column: RawKeyColumns.PublisherId,
        where: {
          type: KeyFilterClause.TypeEnum.Equals,
          value: user?.publisherId,
        },
        onlyFilter: true,
      });
    } else if (formValues.publishers && formValues.publishers.length > 0) {
      if (formValues.publishers.length === 1) {
        res.push({
          column: RawKeyColumns.PublisherId,
          where: {
            type: KeyFilterClause.TypeEnum.Equals,
            value: formValues.publishers,
          },
          onlyFilter: true,
        });
      } else if (formValues.publishers.length > 1) {
        res.push({
          column: RawKeyColumns.PublisherId,
          where: {
            type: KeyFilterClause.TypeEnum.In,
            value: formValues.publishers,
          },
          onlyFilter: true,
        });
      }
    }

    if (user?.role === AccountBean.RoleEnum.Dsp) {
      res.push({
        column: RawKeyColumns.ValidationDspId,
        where: {
          type: KeyFilterClause.TypeEnum.Equals,
          value: user?.dspId,
        },
        onlyFilter: true,
      });
    } else if (
      user.role == AccountBean.RoleEnum.Dsp &&
      RawReportRequestParameters.ReportTypeEnum.ResponseIds === reportType
    ) {
      res.push({
        column: RawKeyColumns.ValidationDspIdGroup,
      });
      if (formValues.dsps && formValues.dsps.length > 0) {
        if (formValues.dsps.length === 1) {
          res.push({
            column: RawKeyColumns.ValidationDspId,
            where: {
              type: KeyFilterClause.TypeEnum.Equals,
              value: formValues.dsps,
            },
            onlyFilter: true,
          });
        } else if (formValues.dsps.length > 1) {
          res.push({
            column: RawKeyColumns.ValidationDspId,
            where: {
              type: KeyFilterClause.TypeEnum.In,
              value: formValues.dsps,
            },
            onlyFilter: true,
          });
        }
      }
    }

    return res;
  };

  const csvToArray = (text) => {
    const reValid =
      /^\s*(?:'[^'\\]*(?:\\[\S\s][^'\\]*)*'|"[^"\\]*(?:\\[\S\s][^"\\]*)*"|[^,'"\s\\]*(?:\s+[^,'"\s\\]+)*)\s*(?:,\s*(?:'[^'\\]*(?:\\[\S\s][^'\\]*)*'|"[^"\\]*(?:\\[\S\s][^"\\]*)*"|[^,'"\s\\]*(?:\s+[^,'"\s\\]+)*)\s*)*$/;
    const reValue =
      /(?!\s*$)\s*(?:'([^'\\]*(?:\\[\S\s][^'\\]*)*)'|"([^"\\]*(?:\\[\S\s][^"\\]*)*)"|([^,'"\s\\]*(?:\s+[^,'"\s\\]+)*))\s*(?:,|$)/g;

    if (!reValid.test(text)) {
      return undefined;
    }
    const a: string[] = [];
    text.replace(reValue, (m0: any, m1: any, m2: any, m3: any) => {
      if (m1 !== undefined) {
        a.push(m1.replace(/\\'/g, "'"));
      } else if (m2 !== undefined) {
        a.push(m2.replace(/\\"/g, "'"));
      } else if (m3 !== undefined) {
        a.push(m3);
      }
    });
    if (/,\s*$/.test(text)) {
      a.push("");
    }
    return a;
  };

  const getIds = async (
    reportType: RawReportRequestParameters.ReportTypeEnum
  ) => {
    setLoading(true);
    setError(undefined);
    setBlobCsv(undefined);
    setData(undefined);
    const values = await form.validateFields();

    const groupCols = values.publishers as string[];
    let groupKeyCols = getGroupKeyColumns(groupCols, values, reportType);

    groupKeyCols.push({
      column:
        RawReportRequestParameters.ReportTypeEnum.ResponseIds === reportType
          ? RawKeyColumns.ValidationResponseIdGroup
          : RawKeyColumns.ValidationBidRequestIdGroup,
    });

    const uuid = await reportApi
      .createRaw({ keyColumns: groupKeyCols, valueColumns: [], reportType })
      .then((resp) => resp.data);
    if (!uuid) {
      setError(
        "The limit for generating these types of reports may have been reached." as any
      );
      setLoading(false);
      return;
    }
    let interval = setInterval(async () => {
      try {
        const response = await reportApi.get(uuid ?? "");
        clearInterval(interval);
        const blob = await response.blob();
        const text = await blob.text();
        if (text.length < 1) {
          setLoading(false);
          return;
        }
        const rows = text.split("\n");
        if (rows.length < 2) {
          setLoading(false);
          return;
        }
        const headerLine = csvToArray(rows[0]);
        setHeader(headerLine);
        const index = headerLine?.findIndex((h) => h === "Dsps bids");
        rows.shift();
        rows.pop();
        const main = rows.map((row) => {
          const mainLine = csvToArray(row);
          if (index && index !== -1 && mainLine) {
            mainLine[index] = mainLine[index]
              .replace("[", "")
              .replace("]", "")
              .split(",")
              .map((line) => line.split(";").join(" "))
              .join("\n\n");
          }
          return mainLine ?? [];
        });
        setData(main);
        setLoading(false);
        setBlobCsv(blob as any);
      } catch (response) {
        if (typeof response === "object") {
          if (response.status !== 425) {
            setLoading(false);
            clearInterval(interval);
          }
          if (response.status === 400) {
            setError(response.statusText);
          }
        }
      }
    }, 500);
  };

  const headerFunc = (headerToProcess?: any[]) => {
    return headerToProcess?.map((h) => ({
      title: (
        <Tooltip title={headerDescription.find((hd) => hd.h === h)?.d}>
          {h}
        </Tooltip>
      ),
      dataIndex: h.toLowerCase(),
      key: h.toLowerCase(),
    }));
  };

  const sourceFunc = (dataToProcess?: any[], headerToProcess?: any[]) => {
    return dataToProcess?.map(
      (row) =>
        headerToProcess?.reduce(
          (o, key, index) => ({
            ...o,
            [key?.toLowerCase() ?? ""]: row[index],
            key: index,
          }),
          {}
        ) ?? {}
    );
  };

  const download = () => {
    // if (navigator.msSaveBlob) { // IE 10+
    //     navigator.msSaveBlob(blobCsv, 'report.csv');
    // } else {
    var link = document.createElement("a");
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      var url = URL.createObjectURL(blobCsv);
      link.setAttribute("href", url);
      link.setAttribute("download", "report.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // }
    }
  };

  const handleDates = (e) => {
    setDates(e);
  };

  const changeDsp = (e) => {
    setDisableRequestId(e.length > 0);
  };

  useEffect(() => {
    form.setFieldsValue({ dates: dates });
  }, [dates]);

  return (
    <div>
      {loading}
      {error && (
        <div>
          <Alert message={error} type="error" />
          <br />
        </div>
      )}
      <Form
        style={{
          maxWidth: "1000px",
        }}
        form={form}
        labelCol={{ span: 3 }}
        wrapperCol={{ span: 21 }}
      >
        <FormItem
          label="Dates"
          name="dates"
          rules={[
            {
              required: true,
              message: "Please choose dates",
            },
          ]}
        >
          <RangePicker
            value={dates}
            format="YYYY-MM-DD HH:00"
            onChange={handleDates}
            showTime={{ format: "YYYY-MM-DD HH:00" }}
          />
        </FormItem>

        {(admin || reporting) && (
          <FormItem
            name="publishers"
            label="Publishers"
            tooltip="Select publishers to generate report with. If none chosen, all publishers will be selected"
          >
            <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Select publishers"
              listHeight={400}
            >
              {info.publishers.map((item: PublisherBean) => (
                <Option key={item.id} value={item.id as number}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormItem>
        )}

        {(admin || reporting) && (
          <FormItem
            name="dsps"
            label="Dsps"
            tooltip="Select dsps to generate ids with. If none chosen, all dsps will be selected"
          >
            <Select
              showSearch
              allowClear
              mode="multiple"
              placeholder="Select dsps"
              listHeight={400}
              onChange={changeDsp}
            >
              {info.dspList.map((item: DspBean) => (
                <Option key={item.id} value={item.id as number}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormItem>
        )}

        <FormItem
          wrapperCol={{
            offset: 3,
            span: 21,
          }}
        >
          {AccountBean.RoleEnum.Dsp != user?.role && (
            <Button
              type="primary"
              htmlType="button"
              loading={loading}
              onClick={() =>
                getIds(RawReportRequestParameters.ReportTypeEnum.RequestIds)
              }
              disabled={disableRequestId}
            >
              Get bidRequest ids
            </Button>
          )}
          <Button
            style={{
              margin: "0 0 0 1%",
            }}
            type="primary"
            htmlType="button"
            loading={loading}
            onClick={() =>
              getIds(RawReportRequestParameters.ReportTypeEnum.ResponseIds)
            }
          >
            Get bidResponse ids
          </Button>
        </FormItem>
      </Form>

      {blobCsv && (
        <Table
          style={{ whiteSpace: "pre-wrap" }}
          scroll={{
            x: calculateColumnsWidth(
              headerFunc(header) ?? [],
              sourceFunc(data, header) ?? [],
              2000
            ).tableWidth,
            y: 700,
          }}
          columns={
            calculateColumnsWidth(
              headerFunc(header) ?? [],
              sourceFunc(data, header) ?? [],
              2000
            ).columns
          }
          dataSource={
            calculateColumnsWidth(
              headerFunc(header) ?? [],
              sourceFunc(data, header) ?? [],
              2000
            ).source
          }
          footer={
            (data?.length ?? -1) > 0
              ? () => (
                  <Button onClick={download} icon={<DownloadOutlined />}>
                    Download
                  </Button>
                )
              : undefined
          }
        />
      )}
    </div>
  );
};
